import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { errorText, useApi } from '../lib/api';
import { useEnsureDraft } from '../lib/contentConfig';
import { keyPartRulesDefaults } from '../lib/s2/defaults';
import {
  PICKER_STEPS,
  stepOneRate,
  whereWouldItSpawn,
  type WhereSpawnResponse,
} from '../lib/s2Api';

const btn =
  'rounded border-2 border-ink px-3 py-1 text-sm font-semibold hover:bg-caption/50 disabled:opacity-40';
const input = 'w-full rounded border-2 border-ink bg-white px-2 py-1 text-sm';

/** Test cities to tune site kinds against real OSM coverage. */
const CITIES: { name: string; lat: number; lon: number }[] = [
  { name: 'Portland, OR', lat: 45.5202, lon: -122.6742 },
  { name: 'Seattle, WA', lat: 47.6062, lon: -122.3321 },
  { name: 'Boise, ID', lat: 43.615, lon: -116.2023 },
  { name: 'London, UK', lat: 51.5072, lon: -0.1276 },
];

/**
 * "Where would it spawn?" (macguffin.html §5 Admin): pick a point and run the server's site picker for each key part, showing which
 * step each one fell to. How we tune site kinds against real OSM coverage before release. Read-only: nothing is placed.
 */
export function WhereSpawnPage() {
  const { client } = useApi();
  const { draft } = useEnsureDraft();
  const parts = (draft?.bundle.keyParts ?? []).filter((k) => k?.id);
  const far = draft?.bundle.keyPartRules?.farRadiusM ?? keyPartRulesDefaults().farRadiusM;
  const [point, setPoint] = useState({ lat: CITIES[0]!.lat, lon: CITIES[0]!.lon });
  const [only, setOnly] = useState<string[]>([]);
  const run = useMutation<WhereSpawnResponse, Error>({
    mutationFn: () =>
      whereWouldItSpawn(client, { ...point, partIds: only.length ? only : undefined }),
  });
  const notThere = run.error && /^HTTP 404\b|^Not Found\b/.test(run.error.message);
  return (
    <>
      <PageHeader
        title="Where would it spawn?"
        description="Pick a point and run the key-part site picker for each part: which step it fell to and which building it chose. Nothing is placed."
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        <div className="space-y-3 rounded border-2 border-ink bg-white p-3">
          <label className="block text-sm">
            Test city
            <select
              aria-label="Test city"
              className={input}
              onChange={(e) => {
                const c = CITIES[Number(e.target.value)];
                if (c) setPoint({ lat: c.lat, lon: c.lon });
              }}
            >
              {CITIES.map((c, i) => (
                <option key={c.name} value={i}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm">
              Lat
              <input
                aria-label="Latitude"
                className={input}
                type="number"
                step="any"
                value={point.lat}
                onChange={(e) => setPoint({ ...point, lat: Number(e.target.value) })}
              />
            </label>
            <label className="text-sm">
              Lon
              <input
                aria-label="Longitude"
                className={input}
                type="number"
                step="any"
                value={point.lon}
                onChange={(e) => setPoint({ ...point, lon: Number(e.target.value) })}
              />
            </label>
          </div>
          <fieldset>
            <legend className="text-xs font-semibold uppercase">Parts (none ticked = all)</legend>
            {parts.length === 0 && (
              <p className="text-xs text-bruise">The draft has no key parts.</p>
            )}
            {parts.map((p) => (
              <label key={p.id} className="mr-3 inline-flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={only.includes(p.id!)}
                  onChange={(e) =>
                    setOnly(e.target.checked ? [...only, p.id!] : only.filter((x) => x !== p.id))
                  }
                />
                <span className="font-mono">{p.id}</span>
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            className={btn}
            disabled={run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? 'Running…' : 'Run the picker'}
          </button>
          <p className="text-xs text-bruise">
            Uses the published content and the imported buildings around the point (far radius {far}{' '}
            m).
          </p>
          {run.error &&
            (notThere ? (
              <p className="text-sm text-bruise">
                Not available yet: this needs the S2-KEY API (
                <code>POST /admin/key-parts/where</code>)
              </p>
            ) : (
              <p className="text-sm text-blam">{errorText(run.error)}</p>
            ))}
        </div>
        {run.data && <SpawnResult data={run.data} far={far} center={point} />}
      </div>
    </>
  );
}

function SpawnResult({
  data,
  far,
  center,
}: {
  data: WhereSpawnResponse;
  far: number;
  center: { lat: number; lon: number };
}) {
  const mLat = 111_320;
  const mLon = 111_320 * Math.cos((center.lat * Math.PI) / 180);
  const r = far * 1.1;
  const rate = stepOneRate(data.parts);
  return (
    <div className="rounded border-2 border-ink bg-white p-3">
      <p className="text-sm">
        Step 1 hit rate: <span className="font-semibold">{Math.round(rate * 100)}%</span> of{' '}
        {data.parts.length} part(s) · {data.buildingsScanned} buildings looked at
      </p>
      <div className="mt-2 grid gap-3 md:grid-cols-2">
        <svg
          viewBox={`${-r} ${-r} ${r * 2} ${r * 2}`}
          className="aspect-square w-full border-2 border-ink bg-newsprint"
          role="img"
          aria-label="Sites around the point"
        >
          <circle
            cx={0}
            cy={0}
            r={far}
            fill="none"
            stroke="var(--color-ink)"
            strokeDasharray={`${far / 40} ${far / 60}`}
            strokeWidth={far / 300}
          />
          <circle cx={0} cy={0} r={far / 60} fill="var(--color-blam)" />
          {data.parts
            .filter((p) => p.lat != null && p.lon != null)
            .map((p) => {
              const x = (p.lon! - center.lon) * mLon;
              const y = -(p.lat! - center.lat) * mLat;
              return (
                <g key={p.partId}>
                  <circle
                    cx={x}
                    cy={y}
                    r={far / 45}
                    fill={
                      p.step === 1
                        ? 'var(--color-benday)'
                        : p.step >= 5
                          ? 'var(--color-blam)'
                          : 'var(--color-caption)'
                    }
                    stroke="var(--color-ink)"
                    strokeWidth={far / 600}
                  />
                  <text x={x + far / 35} y={y} fontSize={far / 18}>
                    {p.partId} ({p.step})
                  </text>
                </g>
              );
            })}
        </svg>
        <table className="w-full self-start text-xs">
          <thead className="text-bruise">
            <tr>
              <th className="text-left">Part</th>
              <th className="text-left">Step</th>
              <th className="text-left">Site</th>
              <th className="text-left">Distance</th>
            </tr>
          </thead>
          <tbody>
            {data.parts.map((p) => (
              <tr key={p.partId} className="border-t border-newsprint align-top">
                <td className="font-mono">{p.partId}</td>
                <td>
                  <span
                    className={`font-semibold ${p.step === 1 ? '' : p.step >= 5 ? 'text-blam' : 'text-bruise'}`}
                  >
                    {p.step}
                  </span>{' '}
                  {p.stepName || PICKER_STEPS[p.step] || ''}
                </td>
                <td>
                  {p.virtualSiteId ? (
                    <span>virtual {p.virtualSiteId}</span>
                  ) : (
                    <>
                      {p.buildingName ?? 'unnamed'}{' '}
                      <span className="font-mono text-bruise">#{p.buildingId}</span>
                      {p.siteKindId && <div className="font-mono text-bruise">{p.siteKindId}</div>}
                    </>
                  )}
                </td>
                <td>{p.distanceM == null ? '' : `${Math.round(p.distanceM)} m`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
